import React from 'react';
import TextCell from './TextCell';
import SortHeaderCell from './SortHeaderCell';
import { Table, Column, Cell } from 'fixed-data-table';
import * as actions from '../redux/actions';
import { ModalContainer, ModalDialog } from 'react-modal-dialog';
import DataTableUtil from '../DataTableUtil';
import Storage from '../Storage';
import { SortTypes } from '../Constants';
import AppInfo from '../model/AppInfo';
import AppInfoForm from './AppInfoForm';

const FILTER_COLUMNS = [
  'name',
  'hostname',
  'checkURL',
  'expectedStatusCode',
  'description',
  'checkStatusCode',
  'checkResult',
  'checkLocalDateTime',
  'checkXAgo',
];
const DEFAULT_COL_SORT_DIRS = { lastUpdatedDateTime: SortTypes.DESC };
const DEFAULT_TABLE_WIDTH = 1260;
const DEFAULT_TABLE_HEIGHT = 500;

function getFilterKey(tableTitle = 'AppInfoTable') {
  return tableTitle + ' : filterText';
}

function getColSortKey(tableTitle = 'AppInfoTable') {
  return tableTitle + ' : columnSortDirs';
}

class AppInfoTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      filterText: Storage.getSessionItem(getFilterKey(props.tableTitle)) || '',
      columnSortDirs:
        Storage.getSessionItem(getColSortKey(props.tableTitle)) || DEFAULT_COL_SORT_DIRS,
      isShowingCreateModal: false,
      selectedRow: new AppInfo(),
    };

    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
    this.handleCloseCreateModal = this.handleCloseCreateModal.bind(this);
    this.handleShowCreateModal = this.handleShowCreateModal.bind(this);
    this.handleCreateFormSubmit = this.handleCreateFormSubmit.bind(this);
    this.handleCloseEditModal = this.handleCloseEditModal.bind(this);
    this.handleShowEditModal = this.handleShowEditModal.bind(this);
    this.handleEditFormSubmit = this.handleEditFormSubmit.bind(this);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.handleCheckClick = this.handleCheckClick.bind(this);
  }

  componentDidMount() {
    let { dispatch } = this.props;
    dispatch(actions.getAppInfoList());
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  handleSortChange(columnKey, sortDir) {
    let value = { [columnKey]: sortDir };
    this.setState({ columnSortDirs: value }, () => {
      Storage.setSessionItem(getColSortKey(this.props.tableTitle), value);
    });
  }

  handleFilter(e) {
    if (!e.target.value) {
      this.setState({ filterText: '' }, () => {
        Storage.setSessionItem(getFilterKey(this.props.tableTitle), '');
      });
      return;
    }

    const filterBy = this.refs.filterInput.value.toLowerCase();
    this.setState({ filterText: filterBy }, () => {
      Storage.setSessionItem(getFilterKey(this.props.tableTitle), filterBy);
    });
  }

  handleCloseCreateModal() {
    this.setState({ isShowingCreateModal: false });
  }

  handleShowCreateModal() {
    let {
      auth: { user },
    } = this.props;
    if (user.isSuperRole()) {
      this.setState({ isShowingCreateModal: true });
    }
  }

  handleCreateFormSubmit(values) {
    // console.log('handleCreateFormSubmit', values);
    const { dispatch } = this.props;
    return dispatch(actions.checkAndCreateAppInfo(new AppInfo(values)))
      .then((appInfo) => {
        if (this.unmounted) return;
        this.handleCloseCreateModal();
      })
      .catch((e) => {
        alert('Error creating! ' + e.message);
      });
  }

  handleCloseEditModal() {
    this.setState({ selectedRow: new AppInfo() });
  }

  handleShowEditModal(appInfo) {
    let {
      auth: { user },
    } = this.props;
    if (user.isSuperRole()) {
      this.setState({ selectedRow: appInfo });
    }
  }

  handleEditFormSubmit(values) {
    // console.log('handleEditFormSubmit', values);
    const { dispatch } = this.props;
    return dispatch(actions.updateAppInfo(new AppInfo(values)))
      .then((appInfo) => {
        if (this.unmounted) return;
        this.handleCloseEditModal();
      })
      .catch((e) => {
        alert('Error updating! ' + e.message);
      });
  }

  handleDeleteClick(id) {
    const { dispatch } = this.props;
    return dispatch(actions.deleteAppInfo(id))
      .then(() => {
        if (this.unmounted) return;
        this.handleCloseEditModal();
      })
      .catch((e) => {
        console.error(e);
        alert('Error deleting! ' + e.message);
      });
  }

  handleCheckClick(id, checkURL) {
    const { dispatch } = this.props;
    return dispatch(actions.checkAndUpdateResult({ id }))
      .then(() => {
        if (this.unmounted) return;
        this.handleCloseEditModal();
      })
      .catch((e) => {
        console.error(e);
        alert('Error checking! ' + e.message);
      });
  }

  render() {
    const { filterText, columnSortDirs } = this.state;
    let appInfoList = this.props.appInfoList || [];

    const dataList = DataTableUtil.filterList(
      appInfoList,
      filterText,
      FILTER_COLUMNS,
      columnSortDirs
    );
    const size = appInfoList.length;

    // console.log(this.props.tableTitle + ' render: ' + new Date().toLocaleString());
    let tableWidth = this.props.tableWidth || DEFAULT_TABLE_WIDTH;
    let tableHeight = this.props.tableHeight || DEFAULT_TABLE_HEIGHT;

    let newRow = new AppInfo();
    newRow.isCreate = true;

    let selectedRow = new AppInfo(this.state.selectedRow); // clone it

    let {
      auth: { user },
    } = this.props;
    let isSuper = user.isSuperRole();

    return (
      <div>
        {this.state.isShowingCreateModal && (
          <ModalContainer onClose={this.handleCloseCreateModal}>
            <ModalDialog onClose={this.handleCloseCreateModal}>
              <AppInfoForm initialValues={newRow} onSubmit={this.handleCreateFormSubmit} />
            </ModalDialog>
          </ModalContainer>
        )}

        {selectedRow && selectedRow.name && (
          <ModalContainer onClose={this.handleCloseEditModal}>
            <ModalDialog onClose={this.handleCloseEditModal}>
              <AppInfoForm
                initialValues={selectedRow}
                onSubmit={this.handleEditFormSubmit}
                onDelete={this.handleDeleteClick}
                onCheck={this.handleCheckClick}
              />
            </ModalDialog>
          </ModalContainer>
        )}

        <div style={{ display: 'block', margin: '20px auto', width: tableWidth }}>
          <div className='pure-g pure-u-1 card-label button-secondary'>
            {isSuper && (
              <button
                className='pure-button button-title button-aux'
                onClick={this.handleShowCreateModal}
              >
                <i className='fa fa-fw fa-plus' />
              </button>
            )}
            <i className='fa fa-fw fa-list-alt' />
            {this.props.tableTitle}
            <input
              className='table-search'
              type='search'
              placeholder='Search'
              ref='filterInput'
              value={filterText}
              onChange={this.handleFilter}
            />
          </div>
          <Table
            rowsCount={dataList.length}
            rowHeight={50}
            headerHeight={50}
            width={tableWidth}
            height={tableHeight}
            {...this.props}
          >
            <Column
              header={<Cell>#</Cell>}
              cell={(props) => (
                <Cell {...props}>
                  <span style={{ fontSize: 10 }}>{props.rowIndex + 1}</span>
                </Cell>
              )}
              fixed={true}
              width={30}
              align='right'
            />
            <Column
              columnKey='name'
              header={
                <SortHeaderCell onSortChange={this.handleSortChange} sortDir={columnSortDirs.name}>
                  Name
                </SortHeaderCell>
              }
              cell={(props) => (
                <Cell {...props}>
                  <span style={{ fontWeight: 600 }}>{dataList[props.rowIndex].name}</span>
                </Cell>
              )}
              fixed={true}
              width={120}
            />
            <Column
              columnKey='hostname'
              header={
                <SortHeaderCell
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.hostname}
                >
                  Hostname
                </SortHeaderCell>
              }
              cell={(props) => (
                <Cell {...props}>
                  <span style={{ fontWeight: 600 }}>{dataList[props.rowIndex].hostname}</span>
                </Cell>
              )}
              fixed={true}
              width={120}
            />
            <Column
              columnKey='checkURL'
              header={
                <SortHeaderCell
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.checkURL}
                >
                  Check URL
                </SortHeaderCell>
              }
              cell={(props) => (
                <Cell
                  {...props}
                  onClick={this.handleShowEditModal.bind(this, dataList[props.rowIndex])}
                >
                  <span style={{ fontWeight: 600, cursor: 'pointer', color: 'blue' }}>
                    {dataList[props.rowIndex].checkURL}
                  </span>
                </Cell>
              )}
              fixed={true}
              width={350}
            />
            <Column
              columnKey='description'
              header={
                <SortHeaderCell
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.description}
                >
                  Description
                </SortHeaderCell>
              }
              cell={<TextCell dataList={dataList} field='description' />}
              fixed={true}
              width={200}
            />
            <Column
              columnKey='expectedStatusCode'
              header={
                <SortHeaderCell
                  style={{ fontSize: 10 }}
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.checkStatusCode}
                >
                  Expected Status Code
                </SortHeaderCell>
              }
              cell={<TextCell dataList={dataList} field='expectedStatusCode' />}
              fixed={true}
              width={50}
            />
            <Column
              columnKey='checkStatusCode'
              header={
                <SortHeaderCell
                  style={{ fontSize: 10 }}
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.checkStatusCode}
                >
                  Check Status Code
                </SortHeaderCell>
              }
              cell={<TextCell dataList={dataList} field='checkStatusCode' />}
              fixed={true}
              width={50}
            />
            <Column
              columnKey='checkResult'
              header={
                <SortHeaderCell
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.checkResult}
                >
                  Check Result
                </SortHeaderCell>
              }
              cell={<TextCell dataList={dataList} field='checkResult' />}
              fixed={true}
              width={200}
            />
            <Column
              columnKey='checkLocalDateTime'
              header={
                <SortHeaderCell
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.checkLocalDateTime}
                >
                  Last Checked
                </SortHeaderCell>
              }
              cell={
                <TextCell
                  dataList={dataList}
                  field='checkXAgo'
                  hoverTextField='checkLocalDateTime'
                />
              }
              fixed={true}
              width={80}
            />
            <Column
              columnKey='isDisabled'
              header={
                <SortHeaderCell
                  style={{ fontSize: 10 }}
                  onSortChange={this.handleSortChange}
                  sortDir={columnSortDirs.isDisabled}
                >
                  Disabled?
                </SortHeaderCell>
              }
              cell={<TextCell dataList={dataList} field='isDisabled' isYN={true} />}
              fixed={true}
              width={60}
            />
          </Table>
          <div className='pure-g' style={{ padding: '5px 0' }}>
            <div className='pure-u-1-3'>
              {dataList.length < size ? (
                <span>
                  Showing {dataList.length} entries (filtered from {size} total entries)
                </span>
              ) : (
                <span>
                  Showing {dataList.length} of {size}
                </span>
              )}
            </div>
            <div className='pure-u-1-3'></div>
            <div className='pure-u-1-3' style={{ textAlign: 'right' }}></div>
          </div>
        </div>
      </div>
    );
  }
}

export default AppInfoTable;



// WEBPACK FOOTER //
// ./src/components/AppInfoTable.js